import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FreeAgentGoalie } from './types';
import { ArrowUpDown, ArrowUp, ArrowDown, Star, Ban as Bandage, Eye, EyeOff, Plus, Clock } from 'lucide-react';
import { translate } from '../../lib/translations';
import { usePlayerCooldown } from '../../lib/hooks/usePlayerCooldown';
import { InjuryDescriptionModal } from '../Modals/InjuryDescriptionModal';

const formatShortName = (fullName: string) => {
  const normalized = (fullName || '').trim().replace(/\s+/g, ' ');
  if (!normalized) return '';
  const parts = normalized.split(' ');
  if (parts.length < 2) return normalized;
  const firstInitial = parts[0]?.[0]?.toUpperCase() || '';
  const lastName = parts[parts.length - 1] || '';
  if (!firstInitial || !lastName) return normalized;
  return `${firstInitial}.${lastName}`;
};

const formatCompactSalaryValue = (amount: number) => {
  const abs = Math.abs(amount);
  if (abs >= 1_000_000) {
    const millions = amount / 1_000_000;
    const rounded = Math.round(millions * 10) / 10;
    const text = Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1);
    return `$${text}M`;
  }
  if (abs >= 1_000) {
    const thousands = Math.round(amount / 1_000);
    return `$${thousands}K`;
  }
  return `$${Math.round(amount)}`;
};

type Goalie = FreeAgentGoalie;

interface SortConfig {
  field: keyof Goalie;
  direction: 'asc' | 'desc';
}

interface FreeAgentsGoalieTableProps {
  goalies: Goalie[];
  sortConfig: SortConfig;
  onSort: (field: keyof Goalie) => void;
  onSign: (goalie: Goalie, e?: React.MouseEvent) => void;
  language: 'en' | 'fr';
  isEmbed?: boolean;
  priorityMode?: boolean;
  actionLabel?: string;
  injuredPlayers?: Array<{playerid: number, description: string}>;
  selectedPlayers?: Set<number>;
  bulkPrioritySettings?: Map<number, boolean>;
  onTeamAssignment?: (playerId: number, isNHL: boolean) => void;
  signingInProgress?: Set<number>;
  onViewDetails?: (goalie: Goalie) => void;
  watchlistPlayerIds?: number[];
  onAddToWatchlist?: (goalie: Goalie) => void;
  onRemoveFromWatchlist?: (goalie: Goalie) => void;
  watchlistActionInProgress?: Set<number>;
  showSelectionPercentage?: boolean;
  showTopHorizontalScrollbar?: boolean;
  /**
   * When true, clicking the goalie's name triggers the sign action (equivalent
   * to clicking "Signer" in the actions menu). Used in BuildMyTeam embed mode
   * (Arsenal / TSLH) to simplify the UX. Cooldown and disabled states are still
   * respected because we reuse `handleSignClick`.
   */
  nameActsAsSign?: boolean;
}

interface Column {
  field: keyof Goalie;
  translationKey: string;
  sortable?: boolean;
}

const baseColumns: Column[] = [
  { field: 'goaliefullname', translationKey: 'name', sortable: true },
  { field: 'teamabbrevs', translationKey: 'team' },
  { field: 'gamesplayed', translationKey: 'gp', sortable: true },
  { field: 'wins', translationKey: 'wins', sortable: true },
  { field: 'losses', translationKey: 'losses', sortable: true },
  { field: 'shutouts', translationKey: 'shutouts', sortable: true },
  { field: 'savepct', translationKey: 'save.percentage', sortable: true },
  { field: 'goals', translationKey: 'goals', sortable: true },
  { field: 'assists', translationKey: 'assists', sortable: true },
  { field: 'salary', translationKey: 'salary', sortable: true },
];

const getColumns = (showSelectionPercentage: boolean): Column[] => {
  if (!showSelectionPercentage) return baseColumns;
  return [
    baseColumns[0],
    { field: 'selection_percentage', translationKey: 'selection.percentage', sortable: true },
    ...baseColumns.slice(1),
  ];
};

const formatSelectionPercentage = (value: number | undefined) => `${Math.round(Number(value ?? 0))} %`;

export function FreeAgentsGoalieTable({
  goalies,
  sortConfig,
  onSort,
  onSign,
  language,
  isEmbed = false,
  priorityMode = false,
  actionLabel = 'Sign',
  injuredPlayers = [],
  selectedPlayers,
  bulkPrioritySettings,
  onTeamAssignment,
  signingInProgress,
  onViewDetails,
  watchlistPlayerIds = [],
  onAddToWatchlist,
  onRemoveFromWatchlist,
  watchlistActionInProgress = new Set(),
  showSelectionPercentage = false,
  showTopHorizontalScrollbar = false,
  nameActsAsSign = false
}: FreeAgentsGoalieTableProps) {
  void selectedPlayers;
  void bulkPrioritySettings;
  void onTeamAssignment;
  const { activeCooldowns, startCooldown, getRemainingTime } = usePlayerCooldown();
  const [tooltipInfo, setTooltipInfo] = useState<{ x: number, y: number, playerId: number } | null>(null);
  const [imageErrors, setImageErrors] = useState<{[key: number]: boolean}>({});
  const [showInjuryModal, setShowInjuryModal] = useState(false);
  const [selectedInjury, setSelectedInjury] = useState<{ playerId: number, playerName: string, description: string } | null>(null);

  const [openActionsForId, setOpenActionsForId] = useState<number | null>(null);
  const actionsToggleRef = useRef<HTMLElement | null>(null);
  const actionsMenuRef = useRef<HTMLDivElement | null>(null);
  const topScrollRef = useRef<HTMLDivElement | null>(null);
  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  const syncingScrollRef = useRef(false);
  const [actionsMenuStyle, setActionsMenuStyle] = useState<React.CSSProperties>({});
  const [topScrollbarWidth, setTopScrollbarWidth] = useState(0);
  const columns = getColumns(showSelectionPercentage);

  const syncHorizontalScroll = (source: HTMLDivElement, target: HTMLDivElement | null) => {
    if (!target || syncingScrollRef.current) return;
    syncingScrollRef.current = true;
    target.scrollLeft = source.scrollLeft;
    requestAnimationFrame(() => {
      syncingScrollRef.current = false;
    });
  };

  const renderSortIcon = (field: keyof Goalie, sortable?: boolean) => {
    if (!sortable) return null;

    if (sortConfig.field !== field) {
      return <ArrowUpDown size={16} className="ms-1 text-muted opacity-50" />;
    }

    return sortConfig.direction === 'asc' ? (
      <ArrowUp size={16} className="ms-1 text-primary" />
    ) : (
      <ArrowDown size={16} className="ms-1 text-primary" />
    );
  };

  const handleSignClick = (goalie: Goalie, e: React.MouseEvent) => {
    if (!priorityMode && activeCooldowns.has(goalie.playerid)) {
      e.preventDefault();
      return;
    }

    if (!priorityMode) {
      startCooldown(goalie.playerid);
    }
    onSign(goalie, e);
  };

  const handleImageError = (playerId: number) => {
    setImageErrors(prev => ({...prev, [playerId]: true}));
  };

  const getGoalieImageUrl = (goalie: Goalie) => {
    if (imageErrors[goalie.playerid]) {
      return "https://assets.nhle.com/mugs/nhl/20242025/EDM/8478402.png";
    }
    return `https://assets.nhle.com/mugs/nhl/20242025/${goalie.teamabbrevs}/${goalie.playerid}.png`;
  };

  const handleMouseEnter = (e: React.MouseEvent, playerId: number) => {
    if (activeCooldowns.has(playerId)) {
      const rect = e.currentTarget.getBoundingClientRect();
      setTooltipInfo({
        x: rect.left + window.scrollX,
        y: rect.top + window.scrollY - 25,
        playerId
      });
    }
  };

  const handleMouseLeave = () => {
    setTooltipInfo(null);
  };

  const handleViewDetailsClick = (goalie: Goalie, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onViewDetails?.(goalie);
  };

  const handleAddToWatchlistClick = (goalie: Goalie, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onAddToWatchlist) {
      onAddToWatchlist(goalie);
    }
  };

  const handleRemoveFromWatchlistClick = (goalie: Goalie, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRemoveFromWatchlist) {
      onRemoveFromWatchlist(goalie);
    }
  };

  const isInWatchlist = (playerId: number) => {
    return watchlistPlayerIds.includes(playerId);
  };

  const getInjuryEntry = (playerId: number) => {
    return injuredPlayers.find(p => p.playerid === playerId);
  };

  const isGoalieInjured = (playerId: number) => {
    return Boolean(getInjuryEntry(playerId));
  };

  const isGoalieReturnedFromInjury = (playerId: number) => {
    const entry = getInjuryEntry(playerId) as any;
    return Boolean(entry?.returned_date || entry?.is_returned);
  };

  const getInjuryDescription = (playerId: number) => {
    const injury = getInjuryEntry(playerId);
    return injury ? injury.description : 'Blessé';
  };

  const handleShowInjuryModal = (goalie: Goalie) => {
    setSelectedInjury({
      playerId: goalie.playerid,
      playerName: goalie.goaliefullname,
      description: getInjuryDescription(goalie.playerid)
    });
    setShowInjuryModal(true);
  };

  useEffect(() => {
    const handleMouseMove = () => {
      if (tooltipInfo) {
        setTooltipInfo(null);
      }
    };

    window.addEventListener('scroll', handleMouseMove);
    
    return () => {
      window.removeEventListener('scroll', handleMouseMove);
    };
  }, [tooltipInfo]);

  const closeActionsMenu = () => {
    setOpenActionsForId(null);
    actionsToggleRef.current = null;
  };

  const updateActionsMenuPosition = () => {
    const toggleEl = actionsToggleRef.current;
    const menuEl = actionsMenuRef.current;
    if (!toggleEl || !menuEl) return;

    const rect = toggleEl.getBoundingClientRect();
    const padding = 8;

    const menuWidth = Math.max(200, menuEl.offsetWidth || 0);
    const naturalMenuHeight = menuEl.offsetHeight || 0;

    const spaceBelow = window.innerHeight - rect.bottom - padding;
    const spaceAbove = rect.top - padding;
    const openUp = spaceBelow < naturalMenuHeight && spaceAbove > spaceBelow;

    const available = Math.max(140, openUp ? spaceAbove : spaceBelow);
    const maxHeight = Math.min(available, window.innerHeight - padding * 2);
    const visibleHeight = Math.min(naturalMenuHeight || maxHeight, maxHeight);

    let top = openUp ? rect.top - visibleHeight : rect.bottom;
    if (top < padding) top = padding;
    if (top + visibleHeight > window.innerHeight - padding) {
      top = window.innerHeight - padding - visibleHeight;
    }

    let left = rect.left;
    if (left + menuWidth > window.innerWidth - padding) {
      left = window.innerWidth - padding - menuWidth;
    }
    if (left < padding) left = padding;

    setActionsMenuStyle({
      position: 'fixed',
      top,
      left,
      zIndex: 7000,
      maxHeight,
      overflowY: 'auto',
      minWidth: 200,
    });
  };

  useLayoutEffect(() => {
    if (openActionsForId === null) return;
    const raf = requestAnimationFrame(() => {
      updateActionsMenuPosition();
    });
    return () => cancelAnimationFrame(raf);
  }, [openActionsForId]);

  useEffect(() => {
    if (openActionsForId === null) return;

    const onDocPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (actionsMenuRef.current?.contains(target)) return;
      if (actionsToggleRef.current?.contains(target)) return;
      closeActionsMenu();
    };

    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeActionsMenu();
    };

    const onScrollOrResize = () => {
      updateActionsMenuPosition();
    };

    document.addEventListener('pointerdown', onDocPointerDown, true);
    document.addEventListener('keydown', onEscape);
    window.addEventListener('resize', onScrollOrResize);
    document.addEventListener('scroll', onScrollOrResize, true);

    return () => {
      document.removeEventListener('pointerdown', onDocPointerDown, true);
      document.removeEventListener('keydown', onEscape);
      window.removeEventListener('resize', onScrollOrResize);
      document.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [openActionsForId]);

  useLayoutEffect(() => {
    if (!showTopHorizontalScrollbar) return;
    const tableScroller = tableScrollRef.current;
    if (!tableScroller) return;

    const updateTopScrollbarWidth = () => {
      setTopScrollbarWidth(tableScroller.scrollWidth);
    };

    updateTopScrollbarWidth();
    window.addEventListener('resize', updateTopScrollbarWidth);

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateTopScrollbarWidth);
      resizeObserver.observe(tableScroller);
      const table = tableScroller.querySelector('table');
      if (table) resizeObserver.observe(table);
    }

    return () => {
      window.removeEventListener('resize', updateTopScrollbarWidth);
      resizeObserver?.disconnect();
    };
  }, [showTopHorizontalScrollbar, goalies.length, columns.length, isEmbed]);

  return (
    <div className="freeagents-table-scroll-sync">
      {showTopHorizontalScrollbar && (
        <div
          ref={topScrollRef}
          className="freeagents-top-scrollbar"
          aria-hidden="true"
          onScroll={(event) => syncHorizontalScroll(event.currentTarget, tableScrollRef.current)}
        >
          <div className="freeagents-top-scrollbar__spacer" style={{ width: topScrollbarWidth }} />
        </div>
      )}
      <div
        ref={tableScrollRef}
        className="table-responsive"
        onScroll={(event) => syncHorizontalScroll(event.currentTarget, topScrollRef.current)}
      >
      <style>{`
        .freeagents-desktop-thead {
          display: none;
        }
        .freeagents-mobile-thead {
          display: table-header-group;
        }
        @media (min-width: 992px) {
          .freeagents-desktop-thead {
            display: table-header-group;
          }
          .freeagents-mobile-thead {
            display: none;
          }
        }
      `}</style>
      {isEmbed && (
        <style>{`
          /* When embedded, force a single header to avoid duplicate thead rows */
          .freeagents-desktop-thead { display: none !important; }
          .freeagents-mobile-thead { display: table-header-group !important; }
        `}</style>
      )}
      <table className="table table-hover table-sm align-middle freeagents-table freeagents-goalies">
        {/* En-tête desktop */}
        {!isEmbed && (
          <thead className="freeagents-desktop-thead">
          <tr>
            <th>Actions</th>
            <th>Photo</th>
            {columns.map((column) => (
              <th
                key={column.field}
                onClick={() => column.sortable && onSort(column.field)}
                style={{ cursor: column.sortable ? 'pointer' : 'default' }}
                className="user-select-none"
              >
                <div className="d-flex align-items-center">
                  {translate(column.translationKey, language)}
                  {renderSortIcon(column.field, column.sortable)}
                </div>
              </th>
            ))}
          </tr>
          </thead>
        )}
        {/* En-tête mobile - Toutes les colonnes avec scroll horizontal */}
        <thead className="freeagents-mobile-thead">
          <tr>
            <th className="freeagents-sticky-actions user-select-none" style={{ fontSize: '0.7rem', padding: '0.25rem', whiteSpace: 'nowrap' }}>Actions</th>
            <th className="freeagents-sticky-photo user-select-none" style={{ fontSize: '0.7rem', padding: '0.25rem', whiteSpace: 'nowrap' }}>Photo</th>
            {columns.map((column) => (
              <th
                key={`mobile-${column.field}`}
                onClick={() => column.sortable && onSort(column.field)}
                style={{ 
                  cursor: column.sortable ? 'pointer' : 'default',
                  fontSize: '0.7rem',
                  padding: '0.25rem',
                  whiteSpace: 'nowrap',
                }}
                className={`user-select-none ${column.field === 'goaliefullname' ? 'freeagents-sticky-name' : ''}`}
              >
                <div className="d-flex align-items-center">
                  {translate(column.translationKey, language)}
                  {renderSortIcon(column.field, column.sortable)}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {goalies.map((goalie) => {
            const tradingBloc = (goalie as any).trading_bloc === true;
            const isIntouchable = (goalie as any).is_intouchable === true;
            const actionsContentDesktop = (
              <div className="freeagents-actions-dropdown">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary dropdown-toggle freeagents-actions-toggle"
                  aria-haspopup="menu"
                  aria-expanded={openActionsForId === goalie.playerid}
                  aria-label={language === 'fr' ? 'Actions' : 'Actions'}
                  title={language === 'fr' ? 'Actions' : 'Actions'}
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    actionsToggleRef.current = e.currentTarget as unknown as HTMLElement;
                    setOpenActionsForId((prev) => (prev === goalie.playerid ? null : goalie.playerid));
                  }}
                ></button>
              </div>
            );

            const actionsContentMobile = (
              <div className="freeagents-actions-dropdown">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary dropdown-toggle freeagents-actions-toggle"
                  aria-haspopup="menu"
                  aria-expanded={openActionsForId === goalie.playerid}
                  aria-label={language === 'fr' ? 'Actions' : 'Actions'}
                  title={language === 'fr' ? 'Actions' : 'Actions'}
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    actionsToggleRef.current = e.currentTarget as unknown as HTMLElement;
                    setOpenActionsForId((prev) => (prev === goalie.playerid ? null : goalie.playerid));
                  }}
                ></button>
              </div>
            );

            return (
              <React.Fragment key={goalie.playerid}>
                {/* Desktop/tablet row */}
                <tr className="d-none d-lg-table-row">
                  <td>{actionsContentDesktop}</td>
                  <td className="position-relative freeagents-photo-cell">
                    <img
                      src={getGoalieImageUrl(goalie)}
                      alt={goalie.goaliefullname}
                      className="border freeagents-photo"
                      onError={() => handleImageError(goalie.playerid)}
                    />
                    {isGoalieInjured(goalie.playerid) && (
                      <span
                        className="position-absolute"
                        style={{ top: '-5px', right: '-5px', cursor: 'pointer' }}
                        onClick={() => handleShowInjuryModal(goalie)}
                      >
                        <Bandage size={16} className="text-danger" />
                      </span>
                    )}
                    {isGoalieReturnedFromInjury(goalie.playerid) && (
                      <span
                        className="position-absolute"
                        style={{ top: '-5px', right: '14px', cursor: 'pointer' }}
                        onClick={() => handleShowInjuryModal(goalie)}
                      >
                        <Clock size={16} className="text-warning" />
                      </span>
                    )}
                  </td>
                  {columns.map((column) => (
                    <td key={`${goalie.playerid}-${column.field}`}>
                      {column.field === 'goaliefullname' && (isGoalieInjured(goalie.playerid) || isGoalieReturnedFromInjury(goalie.playerid)) ? (
                        <div className="d-flex align-items-center">
                          {nameActsAsSign ? (
                            <button
                              type="button"
                              className="btn btn-link p-0 align-baseline text-start"
                              style={{
                                fontSize: 'inherit',
                                fontWeight: 'inherit',
                                color: 'inherit',
                                textDecoration: 'underline',
                              }}
                              disabled={activeCooldowns.has(goalie.playerid) || signingInProgress?.has(goalie.playerid)}
                              onClick={(e) => handleSignClick(goalie, e)}
                              title={`Signer ${goalie.goaliefullname}`}
                              aria-label={`Signer ${goalie.goaliefullname}`}
                            >
                              {goalie.goaliefullname}
                            </button>
                          ) : (
                            goalie.goaliefullname
                          )}
                          {isGoalieInjured(goalie.playerid) && (
                            <Bandage
                              size={16}
                              className="text-danger ms-2"
                              style={{ cursor: 'pointer' }}
                              onClick={() => handleShowInjuryModal(goalie)}
                            />
                          )}
                          {isGoalieReturnedFromInjury(goalie.playerid) && (
                            <Clock
                              size={16}
                              className="text-warning ms-2"
                              style={{ cursor: 'pointer' }}
                              onClick={() => handleShowInjuryModal(goalie)}
                            />
                          )}
                        </div>
                      ) : column.field === 'goaliefullname' && nameActsAsSign ? (
                        <button
                          type="button"
                          className="btn btn-link p-0 align-baseline text-start"
                          style={{
                            fontSize: 'inherit',
                            fontWeight: 'inherit',
                            color: 'inherit',
                            textDecoration: 'underline',
                          }}
                          disabled={activeCooldowns.has(goalie.playerid) || signingInProgress?.has(goalie.playerid)}
                          onClick={(e) => handleSignClick(goalie, e)}
                          title={`Signer ${goalie.goaliefullname}`}
                          aria-label={`Signer ${goalie.goaliefullname}`}
                        >
                          {goalie.goaliefullname}
                        </button>
                      ) : (
                        column.field === 'salary' ? (
                          <span>
                            {new Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency: 'USD',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            }).format(goalie.salary)}
                          </span>
                        ) : column.field === 'selection_percentage' ? (
                          formatSelectionPercentage(goalie.selection_percentage)
                        ) : column.field === 'savepct' ? (
                          goalie.savepct.toFixed(3)
                        ) : (
                          goalie[column.field]
                        )
                      )}
                    </td>
                  ))}
                </tr>

                {/* Mobile row: toutes les colonnes avec scroll horizontal */}
                <tr className="d-table-row d-lg-none">
                  <td className="freeagents-sticky-actions" style={{ fontSize: '0.7rem', padding: '0.25rem', verticalAlign: 'middle' }}>
                    {actionsContentMobile}
                  </td>
                  <td className="freeagents-sticky-photo" style={{ fontSize: '0.7rem', padding: '0.25rem', verticalAlign: 'middle' }}>
                    <span className="position-relative">
                      <img
                        src={getGoalieImageUrl(goalie)}
                        alt={goalie.goaliefullname}
                        className="border"
                        style={{ width: '30px', height: '30px', objectFit: 'cover', borderRadius: '50%' }}
                        onError={() => handleImageError(goalie.playerid)}
                      />
                      {isGoalieInjured(goalie.playerid) && (
                        <span
                          className="position-absolute"
                          style={{ top: '-5px', right: '-5px', cursor: 'pointer' }}
                          onClick={() => handleShowInjuryModal(goalie)}
                        >
                          <Bandage size={12} className="text-danger" />
                        </span>
                      )}
                      {isGoalieReturnedFromInjury(goalie.playerid) && (
                        <span
                          className="position-absolute"
                          style={{ top: '-5px', right: '10px', cursor: 'pointer' }}
                          onClick={() => handleShowInjuryModal(goalie)}
                        >
                          <Clock size={12} className="text-warning" />
                        </span>
                      )}
                    </span>
                  </td>
                  {columns.map((column) => (
                    <td
                      key={`mobile-cell-${goalie.playerid}-${column.field}`}
                      className={column.field === 'goaliefullname' ? 'freeagents-sticky-name' : undefined}
                      style={{
                        fontSize: '0.7rem',
                        padding: '0.25rem',
                        verticalAlign: 'middle',
                        whiteSpace: 'nowrap',
                        textAlign: column.field === 'goaliefullname' ? 'left' : 'center',
                        color: 'var(--text-primary)',
                      }}
                    >
                      {column.field === 'goaliefullname' ? (
                        <div>
                          {nameActsAsSign ? (
                            <button
                              type="button"
                              className="btn btn-link p-0 align-baseline text-start"
                              style={{
                                fontSize: 'inherit',
                                fontWeight: 'inherit',
                                color: 'inherit',
                                textDecoration: 'underline',
                              }}
                              disabled={activeCooldowns.has(goalie.playerid) || signingInProgress?.has(goalie.playerid)}
                              onClick={(e) => handleSignClick(goalie, e)}
                              title={`Signer ${goalie.goaliefullname}`}
                              aria-label={`Signer ${goalie.goaliefullname}`}
                            >
                              {formatShortName(goalie.goaliefullname)}
                            </button>
                          ) : (
                            formatShortName(goalie.goaliefullname)
                          )}
                          {tradingBloc && (
                            <span className="badge bg-success ms-1" style={{ fontSize: '0.6rem' }}>T</span>
                          )}
                          {isIntouchable && (
                            <span className="badge bg-danger ms-1" style={{ fontSize: '0.6rem' }}>I</span>
                          )}
                        </div>
                      ) : column.field === 'salary' ? (
                        formatCompactSalaryValue(goalie.salary)
                      ) : column.field === 'selection_percentage' ? (
                        formatSelectionPercentage(goalie.selection_percentage)
                      ) : column.field === 'savepct' ? (
                        goalie.savepct.toFixed(3)
                      ) : (
                        goalie[column.field]
                      )}
                    </td>
                  ))}
                </tr>

                {openActionsForId === goalie.playerid &&
                  createPortal(
                    <div
                      ref={actionsMenuRef}
                      className="dropdown-menu show"
                      style={actionsMenuStyle}
                      role="menu"
                    >
                      <button
                        type="button"
                        className={`dropdown-item ${priorityMode ? 'text-warning' : 'text-primary'}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSignClick(goalie, e as unknown as React.MouseEvent);
                          closeActionsMenu();
                        }}
                        disabled={!priorityMode && (activeCooldowns.has(goalie.playerid) || signingInProgress?.has(goalie.playerid))}
                      >
                        {priorityMode ? <Star size={14} className="me-2" /> : <Plus size={14} className="me-2" />}
                        {signingInProgress?.has(goalie.playerid)
                          ? (language === 'fr' ? 'En cours...' : 'Signing...')
                          : (language === 'fr' ? 'Signer' : 'Sign')}
                      </button>

                      {onViewDetails && (
                        <button
                          type="button"
                          className="dropdown-item text-success"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetailsClick(goalie, e as unknown as React.MouseEvent);
                            closeActionsMenu();
                          }}
                        >
                          <Eye size={14} className="me-2" />
                          {language === 'fr' ? 'Voir les détails' : 'View details'}
                        </button>
                      )}

                      {(onAddToWatchlist || onRemoveFromWatchlist) && (
                        <button
                          type="button"
                          className={`dropdown-item ${isInWatchlist(goalie.playerid) ? 'text-danger' : 'text-warning'}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isInWatchlist(goalie.playerid)) {
                              handleRemoveFromWatchlistClick(goalie, e as unknown as React.MouseEvent);
                            } else {
                              handleAddToWatchlistClick(goalie, e as unknown as React.MouseEvent);
                            }
                            closeActionsMenu();
                          }}
                          disabled={watchlistActionInProgress.has(goalie.playerid)}
                        >
                          {isInWatchlist(goalie.playerid) ? (
                            <>
                              <EyeOff size={14} className="me-2" />
                              {language === 'fr' ? 'Retirer de la surveillance' : 'Remove from watchlist'}
                            </>
                          ) : (
                            <>
                              <Plus size={14} className="me-2" />
                              {language === 'fr' ? 'Surveiller' : 'Watch'}
                            </>
                          )}
                        </button>
                      )}
                    </div>,
                    document.body
                  )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      </div>
      
      {tooltipInfo && (
        <div
          className="cooldown-tooltip"
          style={{
            top: `${tooltipInfo.y}px`,
            left: `${tooltipInfo.x}px`,
          }}
        >
          {language === 'en' ? 'Cooldown active:' : 'Délai actif:'} {Math.ceil(getRemainingTime(tooltipInfo.playerId) / 1000)}s
        </div>
      )}

      <InjuryDescriptionModal
        show={showInjuryModal}
        onClose={() => setShowInjuryModal(false)}
        playerName={selectedInjury?.playerName || ''}
        description={selectedInjury?.description || ''}
        language={language}
      />
    </div>
  );
}